import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Layout from "@/components/Layout";
import CookieConsent from "@/components/CookieConsent";
import ITRouteGuard from "@/components/auth/ITRouteGuard";
import AdminRouteGuard from "@/components/auth/AdminRouteGuard";
import ActiveVendorRouteGuard from "@/components/auth/ActiveVendorRouteGuard";
import SellerRouteGuard from "@/components/auth/SellerRouteGuard";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Wishlist from "@/pages/Wishlist";
import Account from "@/pages/Account";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import GrievanceRedressal from "@/pages/GrievanceRedressal";
import TermsOfUse from "@/pages/TermsOfUse";
import CancellationRefundPolicy from "@/pages/CancellationRefundPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
const Admin = lazy(() => import("@/pages/Admin"));

const AdminLogin = lazy(() => import("@/pages/AdminLogin"));

const VendorLogin = lazy(() => import("@/pages/VendorLogin"));
const ITLogin = lazy(() => import("@/pages/ITLogin"));
const ITServiceDesk = lazy(() => import("@/pages/ITServiceDesk"));
const ITVault = lazy(() => import("@/pages/ITVaultGate"));
const HelpSupport = lazy(() => import("@/pages/HelpSupport"));
const VendorDashboard = lazy(() => import("@/pages/VendorDashboard"));
const VendorProducts = lazy(() => import("@/pages/VendorProducts"));
const VendorOrders = lazy(() => import("@/pages/VendorOrders"));
const VendorSettlements = lazy(() => import("@/pages/VendorSettlements"));
const VendorPayouts = lazy(() => import("@/pages/VendorPayouts"));
const VendorCompliance = lazy(() => import("@/pages/VendorCompliance"));
const VendorProductForm = lazy(() => import("@/pages/VendorProductForm"));
const SellerBusinessProfile = lazy(() => import("@/pages/SellerBusinessProfile"));
const AdminProductForm = lazy(() => import("@/pages/admin/AdminProductForm"));
const AdminProductView = lazy(() => import("@/pages/admin/AdminProductView"));
import Invoice from "@/pages/Invoice";
import Page from "@/pages/Page";

function RoleRouteGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hostname = window.location.hostname.toLowerCase();

  if (location.pathname === "/" && hostname === "admin.nityavastra.com") {
    return <Navigate to="/admin/login" replace />;
  }

  if (location.pathname === "/" && hostname === "it.nityavastra.com") {
    return <Navigate to="/it/login" replace />;
  }

  if (loading) return children;

  const isITStaff =
    user?.role === "staff" && user?.department === "it_support";

  if (isITStaff && !location.pathname.startsWith("/it")) {
    return <Navigate to="/it" replace />;
  }

  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HelmetProvider>
          <AuthProvider>
            <CartProvider>
              <RoleRouteGuard>
                <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/product/:id" element={<Product />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/admin/login"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading Admin sign in...</div>}>
                        <AdminLogin />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/vendor/login"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading Partner sign in...</div>}>
                        <VendorLogin />
                      </Suspense>
                    }
                  />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/account" element={<Account />} />
                  <Route
                    path="/help-support"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading Help & Support...</div>}>
                        <HelpSupport />
                      </Suspense>
                    }
                  />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/grievance-redressal" element={<GrievanceRedressal />} />
                  <Route path="/terms" element={<TermsOfUse />} />
                  <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />

                  <Route
                    path="/it/login"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading IT sign in...</div>}>
                        <ITLogin />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/it"
                    element={
                      <ITRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading IT Service Desk...</div>}>
                          <ITServiceDesk />
                        </Suspense>
                      </ITRouteGuard>
                    }
                  />
                  <Route
                    path="/it/vault"
                    element={
                      <ITRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading IT Vault...</div>}>
                          <ITVault />
                        </Suspense>
                      </ITRouteGuard>
                    }
                  />
                  <Route path="/admin/products/new" element={<AdminRouteGuard><Navigate to="/admin?tab=stock-in" replace /></AdminRouteGuard>} />
                  <Route
                    path="/admin/products/:id/edit"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading product form...</div>}>
                        <AdminProductForm />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/admin/products/:id/view"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading product...</div>}>
                        <AdminProductView />
                      </Suspense>
                    }
                  />

                  <Route
                    path="/admin/help-support"
                    element={
                      <AdminRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading Help & Support...</div>}>
                          <HelpSupport />
                        </Suspense>
                      </AdminRouteGuard>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading admin console...</div>}>
                          <Admin />
                        </Suspense>
                      </AdminRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/help-support"
                    element={
                      <SellerRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading Help & Support...</div>}>
                          <HelpSupport />
                        </Suspense>
                      </SellerRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/dashboard"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading vendor portal...</div>}>
                          <VendorDashboard />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />
                  <Route path="/vendor/products/new" element={<SellerRouteGuard><Navigate to="/vendor/products" replace /></SellerRouteGuard>} />
                  <Route
                    path="/vendor/products/:productId/edit"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading product form...</div>}>
                          <VendorProductForm />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />

                  <Route
                    path="/vendor/orders"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading vendor orders...</div>}>
                          <VendorOrders />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />

                  <Route
                    path="/vendor/settlements"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading vendor settlements...</div>}>
                          <VendorSettlements />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/payouts"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading vendor payouts...</div>}>
                          <VendorPayouts />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/compliance"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading compliance...</div>}>
                          <VendorCompliance />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/products"
                    element={
                      <ActiveVendorRouteGuard>
                        <Suspense fallback={<div className="p-8 text-center">Loading vendor products...</div>}>
                          <VendorProducts />
                        </Suspense>
                      </ActiveVendorRouteGuard>
                    }
                  />
                  <Route
                    path="/vendor/business-profile"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading business profile...</div>}>
                        <SellerBusinessProfile />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/vendor/onboarding"
                    element={
                      <Suspense fallback={<div className="p-8 text-center">Loading partner onboarding...</div>}>
                        <SellerBusinessProfile />
                      </Suspense>
                    }
                  />
                  <Route path="/invoice/:id" element={<Invoice />} />
                  <Route path="/pages/contact" element={<Navigate to="/contact" replace />} />
                  <Route path="/pages/:slug" element={<Page />} />
                </Routes>
                </Layout>
              </RoleRouteGuard>
              <CookieConsent />
              <Toaster position="top-center" richColors />
            </CartProvider>
          </AuthProvider>
        </HelmetProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
